import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, LayoutList, Loader2, Save } from 'lucide-react';
import { adminQuotesAPI } from '../../services/api';

/**
 * Quotes → Settings → Quote layout: how a ConnectWise quote reads on the
 * shared link and in the portal (services/quotes/presentation.js).
 */
export default function QuoteLayoutSettingsCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['quote-presentation'],
    queryFn: () => adminQuotesAPI.getPresentation().then((r) => r.data),
  });
  const [form, setForm] = useState(null);
  const [state, setState] = useState({ busy: false, error: '', saved: false });

  useEffect(() => { if (data?.settings) setForm(data.settings); }, [data]);

  if (isLoading || (!form && !loadError)) {
    return <div className="p-6 flex items-center gap-2 text-neutral-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }
  if (loadError) {
    return <div className="p-4 text-sm text-red-700 bg-red-50 rounded-lg">The quote layout settings could not be loaded.</div>;
  }

  const set = (patch) => { setForm({ ...form, ...patch }); setState({ busy: false, error: '', saved: false }); };
  const save = async () => {
    setState({ busy: true, error: '', saved: false });
    try {
      const res = await adminQuotesAPI.updatePresentation(form);
      setForm(res.data.settings);
      queryClient.invalidateQueries({ queryKey: ['quote-presentation'] });
      setState({ busy: false, error: '', saved: true });
    } catch (e) {
      setState({ busy: false, error: e.response?.data?.error || 'Could not save.', saved: false });
    }
  };
  const label = 'block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1';
  const help = 'text-xs text-neutral-500 dark:text-neutral-400 mt-1';
  const input = 'w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <LayoutList className="w-5 h-5 text-neutral-400" />
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Quote layout</h2>
        </div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          How a ConnectWise quote reads for your clients, on the shared link and in the portal.
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <span className={label}>Group lines into sections by</span>
            <select value={form.groupBy} onChange={(e) => set({ groupBy: e.target.value })} className={input}>
              <option value="type">Line type (Products, Services…)</option>
              <option value="category">Catalog category (Hardware, Software…)</option>
              <option value="none">Nothing: one list</option>
            </select>
            <p className={help}>Each section gets its own subtotal. Recurring lines always have a section of their own, so what is due now and what bills every cycle are never added together.</p>
          </div>
          <div>
            <span className={label}>Shipping amount custom field</span>
            <input value={form.shippingAmountField} onChange={(e) => set({ shippingAmountField: e.target.value })} className={input} maxLength={100} placeholder="Shipping Amount" />
            <p className={help}>The caption of an opportunity custom field holding the shipping charge, if you map one from QuoteWerks. Lines named &quot;Shipping&quot; or &quot;Freight&quot; are shown as shipping either way.</p>
          </div>
          <div>
            <span className={label}>Purchase order number when accepting</span>
            <select value={form.poField} onChange={(e) => set({ poField: e.target.value })} className={input}>
              <option value="optional">Ask, optional</option>
              <option value="required">Ask, required</option>
              <option value="off">Don&apos;t ask</option>
            </select>
            <p className={help}>The number is printed on the signed copy, added to the acceptance note in ConnectWise, and used as the customer PO on the agreement the quote creates.</p>
          </div>
        </div>

        <div className="space-y-2 text-sm text-neutral-700 dark:text-neutral-200">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={form.showPartNumbers} onChange={(e) => set({ showPartNumbers: e.target.checked })} className="mt-0.5" />
            <span>Show each product&apos;s part number and manufacturer from the ConnectWise catalog</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={form.showSalesRep} onChange={(e) => set({ showSalesRep: e.target.checked })} className="mt-0.5" />
            <span>Show the opportunity&apos;s sales rep as the client&apos;s account manager, with their office email and phone from ConnectWise</span>
          </label>
        </div>

        <div className="space-y-3">
          <div>
            <span className={label}>About your company: heading</span>
            <input value={form.aboutTitle} onChange={(e) => set({ aboutTitle: e.target.value })} className={input} maxLength={120} placeholder="Why work with us" />
          </div>
          <div>
            <span className={label}>About your company: text</span>
            <textarea value={form.aboutText} onChange={(e) => set({ aboutText: e.target.value })} rows={6} className={input} maxLength={5000}
              placeholder="A few short paragraphs: who you are, how you support clients, what happens after they accept." />
            <p className={help}>Shown above the lines on every quote. Plain text; a blank line starts a new paragraph. Leave it empty to show nothing. {form.aboutText.length}/5000</p>
          </div>
        </div>

        {state.error && (
          <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200"><AlertCircle className="w-4 h-4 mt-0.5" /> {state.error}</div>
        )}
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={state.busy} className="btn-primary inline-flex items-center gap-2">
            {state.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save layout
          </button>
          {state.saved && <span className="inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-400"><CheckCircle className="w-4 h-4" /> Saved</span>}
        </div>
      </div>
    </div>
  );
}
